import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil, combineLatest, distinctUntilChanged, filter } from 'rxjs';

import { CustomerStatusService, StatusOption, StatusDropdownOption, CustomerStatusUpdateRequest } from '../../services/customer-status.service';
import { ToastService } from '@core/services/toast.service';
import { PermissionService } from '@shared/permission-service.ts/resorces-permission.service';
import { CustomerDataRefreshService } from '@shared/services/customer-data-refresh.service';
import { CommonDropdownComponent } from '@shared/components/common-dropdown/common-dropdown.component';

// Interface for customer status permissions
interface CustomerStatusPermissions {
  new: boolean;
  active: boolean;
  suspended: boolean;
  deactivated: boolean;
  pendingActivation: boolean;
}

export interface CustomerData {
  customerId: string;
  name: string;
  mdn: string;
  status: string;
  accountType: string;
  customerType?: {
    value: string;
  };
}

@Component({
  selector: 'app-status-change-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, CommonDropdownComponent],
  template: `
    <div class="status-change-modal">
      <!-- Modal Header with Icon -->
      <div class="modal-header">
        <div class="header-icon">
          <img src="assets/images/user-avatar.png"/>
        </div>
        <h2 class="modal-title">Change Customer Status</h2>
        <p class="modal-subtitle">This change affects provisioning, billing, and purchase orders. Please review before proceeding.</p>
      </div>

      <!-- Modal Body -->
      <div class="modal-body">
        <!-- Tab Toggle Buttons -->
        <div class="tab-toggle">
          <button
            type="button"
            class="toggle-button"
            [class.active]="activeTab() === 'electronic'"
            (click)="setActiveTab('electronic')">
            Electronics
          </button>
          <button
            type="button"
            class="toggle-button"
            [class.active]="activeTab() === 'non-electronic'"
            (click)="setActiveTab('non-electronic')">
            Non Electronics
          </button>
        </div>

        <!-- Form Content -->
        <form [formGroup]="statusForm" (ngSubmit)="onSubmit()" class="status-form">
          <div class="form-row">
            <!-- Status Selection -->
            <div class="form-group">
                <app-common-dropdown
                  [items]="statusDisplayNames()"
                  [placeholder]="isLoadingDropdowns() ? 'Loading status options...' : 'Select Status'"
                  [selectedItem]="selectedStatusDisplay()"
                  [width]="'100%'"
                  [searchable]="true"
                  [useFixedPosition]="false"
                  (itemSelected)="onStatusSelected($event)">
                </app-common-dropdown>
              <div class="invalid-feedback" *ngIf="statusForm.get('status')?.invalid && statusForm.get('status')?.touched">
                Status selection is required
              </div>
            </div>

            <!-- Conditional Fields (V1 Pattern) -->
            <ng-container *ngIf="activeTab() === 'electronic'">
              <!-- Rejection Reason for Deactivated -->
              <div class="form-group" *ngIf="isStatusDeactivated(selectedStatus())">
                <app-common-dropdown
                  [items]="rejectionReasonNames()"
                  [placeholder]="'Select Rejection Reason'"
                  [selectedItem]="selectedRejectionReasonDisplay()"
                  [width]="'100%'"
                  [searchable]="true"
                  [useFixedPosition]="false"
                  (itemSelected)="onRejectionReasonSelected($event)">
                </app-common-dropdown>
              </div>

              <!-- De-Enrollment Code for Suspend ONLY -->
              <div class="form-group" *ngIf="isStatusSuspended(selectedStatus())">
                <app-common-dropdown
                  [items]="deEnrollCodeNames()"
                  [placeholder]="'Select De-Enrollment Code'"
                  [selectedItem]="selectedDeEnrollCodeDisplay()"
                  [width]="'100%'"
                  [searchable]="true"
                  [useFixedPosition]="false"
                  (itemSelected)="onDeEnrollCodeSelected($event)">
                </app-common-dropdown>
              </div>

              <!-- Termination Codes for UCL Deactivated -->
              <div class="form-group" *ngIf="showTerminationCodes()">
                <app-common-dropdown
                  [items]="terminationCodeNames()"
                  [placeholder]="'Select De-Enrollment Code'"
                  [selectedItem]="selectedTerminationCodeDisplay()"
                  [width]="'100%'"
                  [searchable]="true"
                  [useFixedPosition]="false"
                  (itemSelected)="onTerminationCodeSelected($event)">
                </app-common-dropdown>
              </div>

              <!-- De-Enrollment Code for Deactivated (non-UCL) -->
              <div class="form-group" *ngIf="isStatusDeactivated(selectedStatus()) && !showTerminationCodes()">
                <app-common-dropdown
                  [items]="deEnrollCodeNames()"
                  [placeholder]="'Select De-Enrollment Code'"
                  [selectedItem]="selectedDeEnrollCodeDisplay()"
                  [width]="'100%'"
                  [searchable]="true"
                  [useFixedPosition]="false"
                  (itemSelected)="onDeEnrollCodeSelected($event)">
                </app-common-dropdown>
              </div>

              <!-- Transaction Exception for Active (only if current status is not Active) -->
              <div class="form-group" *ngIf="isStatusActive(selectedStatus()) && customerData?.status !== 'Active'">
                <app-common-dropdown
                  [items]="transExceptionNames()"
                  [placeholder]="'Select Transaction Exception'"
                  [selectedItem]="selectedTransExceptionDisplay()"
                  [width]="'100%'"
                  [searchable]="true"
                  [useFixedPosition]="false"
                  (itemSelected)="onTransExceptionSelected($event)">
                </app-common-dropdown>
              </div>
            </ng-container>
          </div>
        </form>
      </div>

      <!-- Modal Footer -->
      <div class="modal-footer">
        <button
          type="button"
          class="btn btn-cancel"
          (click)="onCancel()"
          [disabled]="isLoading()">
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-confirm"
          (click)="onSubmit()"
          [disabled]="!statusForm.valid || isLoading()">
          <span *ngIf="isLoading()" class="loading-spinner"></span>
          {{ isLoading() ? 'Updating...' : 'Confirm' }}
        </button>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
    }

    .status-change-modal {
      --status-modal-surface: var(--colors-background-card, #ffffff);
      --status-modal-surface-muted: #f9fafb;
      --status-modal-surface-strong: #f0f4f5;
      --status-modal-field-bg: #ffffff;
      --status-modal-field-menu-bg: #ffffff;
      --status-modal-border: #e5e7eb;
      --status-modal-border-hover: #d1d5db;
      --status-modal-text: #111827;
      --status-modal-text-muted: #6b7280;
      --status-modal-tab-text: #6b7280;
      --status-modal-tab-active-text: #111827;
      --status-modal-cancel-bg: #ffffff;
      --status-modal-cancel-hover: #f9fafb;
      --status-modal-cancel-text: #374151;
      --status-modal-confirm-bg: #111827;
      --status-modal-confirm-hover: #1f2937;
      --status-modal-confirm-text: #ffffff;

      display: flex;
      flex-direction: column;
      width: 100%;
      max-width: 480px;
      background: var(--status-modal-surface);
      color: var(--status-modal-text);
      border-radius: 16px;
      overflow: visible;
      padding: 0;
      margin: 0 auto;
    }

    .modal-header {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      padding-bottom: 24px;
    }

    .header-icon {
      width: 72px;
      height: 72px;
      border-radius: 50%;
      background-color: #F0F4F5;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 20px;
    }

    .header-icon svg {
      width: 32px;
      height: 32px;
    }

    .modal-title {
      font-family: 'Lexend', sans-serif;
      font-weight: 400;
      font-size: 22px;
      color: var(--status-modal-text);
      margin: 0 0 8px 0;
    }

    .modal-subtitle {
      font-family: 'Lexend', sans-serif;
      font-weight: 400;
      font-size: 14px;
      color: var(--status-modal-text-muted);
      margin: 0;
      line-height: 1.5;
      max-width: 380px;
    }

    .modal-body {
      flex: 1;
      overflow: visible;
    }

    .tab-toggle {
      display: flex;
      gap: 12px;
      margin-bottom: 20px;
    }

    .toggle-button {
      flex: 1;
      padding: 15px;
      border: 1px solid var(--status-modal-border);
      background: var(--status-modal-surface-muted);
      color: var(--status-modal-tab-text);
      font-family: "Lexend", sans-serif;
      font-weight: 400;
      font-size: 14px;
      cursor: pointer;
      transition: all 0.2s ease;
      border-radius: 8px;
    }

    .toggle-button:hover {
      background-color: var(--status-modal-surface-strong);
      border-color: var(--status-modal-border-hover);
    }

    .toggle-button.active {
      background-color: var(--status-modal-surface-strong);
      border-color: var(--status-modal-border-hover);
      color: var(--status-modal-tab-active-text);
      line-height: 15px;
    }

    .status-form {
      width: 100%;
    }

    .form-row {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .form-group {
      display: flex;
      flex-direction: column;
      position: relative;
    }

    .form-group ::ng-deep .dropdown-container.select-type {
      width: 100% !important;
    }

    .form-group ::ng-deep .dropdown-container.select-type .dropdown-trigger {
      height: 50px;
      border-radius: 8px;
      border: 1px solid var(--status-modal-border);
      background: var(--status-modal-field-bg);
    }

    .form-group ::ng-deep .dropdown-container.select-type .dropdown-menu {
      border-radius: 8px;
      top: calc(100% + 8px);
      max-height: 260px;
      z-index: 9999;
      overflow-x: hidden;
      overflow-y: auto;
      background: var(--status-modal-field-menu-bg);
      border-color: var(--status-modal-border);
      /* Show a visible scrollbar so long option lists are clearly scrollable */
      scrollbar-width: thin;
      -ms-overflow-style: auto;
    }

    .form-group ::ng-deep .dropdown-container.select-type .dropdown-menu::-webkit-scrollbar {
      display: block;
      width: 6px;
    }

    .form-group ::ng-deep .dropdown-container.select-type .dropdown-menu::-webkit-scrollbar-thumb {
      background: var(--status-modal-border-hover);
      border-radius: 3px;
    }

    /* Keep each option fully visible (no vertical clipping of text) */
    .form-group ::ng-deep .dropdown-container.select-type .dropdown-menu .dropdown-item {
      flex: 0 0 auto;
      white-space: normal;
      line-height: 1.4;
    }

    /* Expand modal when dropdown is open */
    .form-group ::ng-deep .dropdown-container.select-type.is-open {
      // margin-bottom: 160px;
    }

    .invalid-feedback {
      font-size: 12px;
      color: #EF4444;
      margin-top: 6px;
      padding-left: 16px;
    }

    .modal-footer {
      display: flex;
      align-items: center;
      gap: 12px;
      padding-top: 28px;
    }

    .btn {
      flex: 1;
      padding: 14px 24px;
      border-radius: 12px;
      font-family: 'Lexend', sans-serif;
      font-weight: 400;
      font-size: 14px;
      border: 1px solid transparent;
      cursor: pointer;
      transition: all 0.2s ease;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }

    .btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .btn-cancel {
      background-color: var(--status-modal-cancel-bg);
      border-color: var(--status-modal-border);
      color: var(--status-modal-cancel-text);
    }

    .btn-cancel:hover:not(:disabled) {
      background-color: var(--status-modal-cancel-hover);
      border-color: var(--status-modal-border-hover);
    }

    .btn-confirm {
      background-color: var(--status-modal-confirm-bg);
      border-color: var(--status-modal-confirm-bg);
      color: var(--status-modal-confirm-text);
    }

    .btn-confirm:hover:not(:disabled) {
      background-color: var(--status-modal-confirm-hover);
      border-color: var(--status-modal-confirm-hover);
    }

    .loading-spinner {
      width: 16px;
      height: 16px;
      border: 2px solid transparent;
      border-top: 2px solid currentColor;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    /* Responsive Design */
    @media (max-width: 768px) {
      .status-change-modal {
        max-width: 100%;
        padding: 24px 0 0;
        border-radius: 0;
      }

      .modal-title {
        font-size: 16px;
      }

      .modal-subtitle {
        font-size: 14px;
      }

      .header-icon {
        width: 60px;
        height: 60px;
      }

      .toggle-button {
        padding: 12px 16px;
        font-size: 13px;
      }

      .btn {
        padding: 12px 20px;
      }

      .modal-footer {
        padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
      }
    }

    :host-context([data-theme='dark']) .status-change-modal,
    :host-context(html.dark) .status-change-modal,
    :host-context(.theme-dark) .status-change-modal,
    :host-context(.dark) .status-change-modal {
      --status-modal-surface: var(--colors-background-card, #1f2937);
      --status-modal-surface-muted: var(--colors-background-secondary, #111827);
      --status-modal-surface-strong: var(--colors-background-tertiary, #273449);
      --status-modal-field-bg: var(--colors-background-secondary, #111827);
      --status-modal-field-menu-bg: var(--colors-background-secondary, #111827);
      --status-modal-border: var(--colors-border-primary, rgba(148, 163, 184, 0.22));
      --status-modal-border-hover: var(--colors-border-hover, rgba(148, 163, 184, 0.38));
      --status-modal-text: var(--colors-text-primary, #f9fafb);
      --status-modal-text-muted: var(--colors-text-secondary, #cbd5e1);
      --status-modal-tab-text: var(--colors-text-secondary, #cbd5e1);
      --status-modal-tab-active-text: var(--colors-text-primary, #f9fafb);
      --status-modal-cancel-bg: transparent;
      --status-modal-cancel-hover: rgba(255, 255, 255, 0.05);
      --status-modal-cancel-text: var(--colors-text-primary, #e5e7eb);
      --status-modal-confirm-bg: #f3f4f6;
      --status-modal-confirm-hover: #ffffff;
      --status-modal-confirm-text: #111827;
    }
  `]
})
export class StatusChangeModalComponent implements OnInit, OnDestroy {
  @Input() customerData?: CustomerData;
  @Output() statusUpdated = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();

  // Services
  private readonly statusService = inject(CustomerStatusService);
  private readonly toastService = inject(ToastService);
  private readonly fb = inject(FormBuilder);
  private readonly permissionService = inject(PermissionService);
  private readonly customerDataRefreshService = inject(CustomerDataRefreshService);
  private readonly destroy$ = new Subject<void>();

  // Component state
  activeTab = signal<'electronic' | 'non-electronic'>('electronic');
  isLoading = signal(false);
  isLoadingDropdowns = signal(false);
  formErrors = signal<{ [key: string]: string }>({});

  // Status permissions
  statusPermissions: CustomerStatusPermissions = {
    new: false,
    active: false,
    suspended: false,
    deactivated: false,
    pendingActivation: false
  };

  // Loading states for individual dropdowns
  private isLoadingTransExceptions = false;
  private isLoadingRejectionReasons = false;
  private isLoadingDeEnrollCodes = false;
  private isLoadingTerminationCodes = false;

  statusForm: FormGroup;

  // Data signals
  statusOptions = signal<StatusOption[]>([]);
  rejectionReasons = signal<StatusDropdownOption[]>([]);
  deEnrollCodes = signal<StatusDropdownOption[]>([]);
  transExceptions = signal<StatusDropdownOption[]>([]);
  terminationCodes = signal<StatusDropdownOption[]>([]);

  // Form value signals
  selectedStatusSignal = signal<string>('');
  selectedRejectionReasonSignal = signal<string>('');
  selectedDeEnrollCodeSignal = signal<string>('');
  selectedTransExceptionSignal = signal<string>('');
  selectedTerminationCodeSignal = signal<string>('');

  // Computed values
  selectedStatus = computed(() => this.selectedStatusSignal());

  showTerminationCodes = computed(() => {
    const status = this.selectedStatus();
    const customerType = this.customerData?.customerType?.value?.toLowerCase() || '';
    return status === 'Deactivated' && customerType.includes('ucl');
  });

  // Computed display names for dropdowns (string arrays)
  statusDisplayNames = computed(() => this.statusOptions().map(opt => opt.DisplayName));
  rejectionReasonNames = computed(() => this.rejectionReasons().map(opt => opt.Name));
  deEnrollCodeNames = computed(() => this.deEnrollCodes().map(opt => opt.Name));
  transExceptionNames = computed(() => this.transExceptions().map(opt => opt.Name));
  terminationCodeNames = computed(() => this.terminationCodes().map(opt => opt.Name));

  // Computed selected display values
  selectedStatusDisplay = computed(() => {
    const statusName = this.selectedStatusSignal();
    const option = this.statusOptions().find(opt => opt.Name === statusName);
    return option?.DisplayName || '';
  });
  selectedRejectionReasonDisplay = computed(() => this.selectedRejectionReasonSignal());
  selectedDeEnrollCodeDisplay = computed(() => this.selectedDeEnrollCodeSignal());
  selectedTransExceptionDisplay = computed(() => this.selectedTransExceptionSignal());
  selectedTerminationCodeDisplay = computed(() => this.selectedTerminationCodeSignal());

  constructor() {
    this.initializeForm();
  }

  async ngOnInit(): Promise<void> {
    // Start loading data immediately — do NOT block on permission check.
    // Without this, users see a blank dropdown whenever the permission check
    // resolves after the HTTP call (race condition).
    this.loadInitialData();
    this.setupFormSubscriptions();
    this.setupLoadingStateWatcher();

    // Resolve permissions in parallel with the data load.
    this.statusPermissions = await this.checkCustomerStatusPermissions();

    // Re-apply the permission filter now that we have real permissions.
    // Handles the case where the HTTP response arrived *before* permissions
    // resolved, causing filterStatusOptionsByPermissions to run with the
    // default all-false permissions and produce an empty list.
    const rawOptions = this.statusService.getCurrentStatusOptions();
    if (rawOptions.length > 0) {
      this.statusOptions.set(this.filterStatusOptionsByPermissions(rawOptions));
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForm(): void {
    this.statusForm = this.fb.group({
      status: ['', [Validators.required]],
      rejectionReason: [''],
      deEnrollCode: [''],
      transException: [''],
      terminationCode: ['']
    });

    // Initialize the status signal
    this.selectedStatusSignal.set('');
  }

  private loadInitialData(): void {
    // Load status options
    console.log("Loading status options...");
    this.statusService.getStatusOptions()
      .pipe(takeUntil(this.destroy$))
      .subscribe(response => {
        if (response.Status) {
          console.log("Loading status options...", response.Status);
          // Filter status options based on permissions
          const filteredStatuses = this.filterStatusOptionsByPermissions(response.data);
          this.statusOptions.set(filteredStatuses);
          console.log("Filtered status options based on permissions:", filteredStatuses);
        }
      });

    // Subscribe to cached data
    combineLatest([
      this.statusService.rejectionReasons$,
      this.statusService.deEnrollCodes$,
      this.statusService.transExceptions$,
      this.statusService.terminationCodes$
    ]).pipe(takeUntil(this.destroy$))
      .subscribe(([rejectionReasons, deEnrollCodes, transExceptions, terminationCodes]) => {
        this.rejectionReasons.set(rejectionReasons);
        this.deEnrollCodes.set(deEnrollCodes);
        this.transExceptions.set(transExceptions);
        this.terminationCodes.set(terminationCodes);
      });
  }

  private setupFormSubscriptions(): void {
    // Watch for status changes to load conditional dropdowns
    this.statusForm.get('status')?.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged()
      )
      .subscribe(status => {
        console.log('Status changed to:', status);
        // Update the signal to trigger reactive updates
        this.selectedStatusSignal.set(status || '');

        // Only trigger API calls for non-empty values
        if (status && status.trim() !== '') {
          this.onStatusChange(status);
        }
      });
  }

  private setupLoadingStateWatcher(): void {
    // Initial form state setup
    this.updateFormState();
  }

  private updateFormState(): void {
    const statusControl = this.statusForm.get('status');
    if (statusControl) {
      if (this.isLoading() || this.isLoadingDropdowns()) {
        if (statusControl.enabled) {
          statusControl.disable();
        }
      } else {
        if (statusControl.disabled) {
          statusControl.enable();
        }
      }
    }
  }

  private onStatusChange(status: string): void {
    // Clear conditional fields when status changes
    this.clearConditionalFields();

    // Set loading state if we need to load dropdowns
    const needsDropdowns = this.isStatusDeactivated(status) || this.isStatusSuspended(status) || this.isStatusActive(status);
    if (needsDropdowns) {
      this.isLoadingDropdowns.set(true);
      this.updateFormState(); // Update form state when loading starts
    }

    // Load required dropdowns based on status
    if (this.isStatusDeactivated(status)) {
      this.loadRejectionReasons();
      this.loadDeEnrollCodes();

      // Load termination codes for UCL customers
      if (this.showTerminationCodes()) {
        this.loadTerminationCodes();
      }
    } else if (this.isStatusSuspended(status)) {
      this.loadDeEnrollCodes();
    } else if (this.isStatusActive(status)) {
      this.loadTransExceptions();
    }

    // Clear loading state after a short delay
    if (needsDropdowns) {
      setTimeout(() => {
        this.isLoadingDropdowns.set(false);
        this.updateFormState(); // Update form state when loading ends
      }, 1000);
    }
  }

  private clearConditionalFields(): void {
    this.statusForm.patchValue({
      rejectionReason: '',
      deEnrollCode: '',
      transException: '',
      terminationCode: ''
    });
  }

  private loadRejectionReasons(): void {
    if (this.statusService.getCurrentRejectionReasons().length === 0 && !this.isLoadingRejectionReasons) {
      this.isLoadingRejectionReasons = true;
      console.log('Loading rejection reasons...');

      this.statusService.getRejectionReasons()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            console.log('Rejection reasons loaded:', response);
            this.isLoadingRejectionReasons = false;
          },
          error: (error) => {
            console.error('Error loading rejection reasons:', error);
            this.isLoadingRejectionReasons = false;
          }
        });
    } else if (this.statusService.getCurrentRejectionReasons().length > 0) {
      console.log('Rejection reasons already loaded, using cache');
    } else {
      console.log('Rejection reasons already loading, skipping duplicate call');
    }
  }

  private loadDeEnrollCodes(): void {
    if (this.statusService.getCurrentDeEnrollCodes().length === 0 && !this.isLoadingDeEnrollCodes) {
      this.isLoadingDeEnrollCodes = true;
      console.log('Loading de-enrollment codes...');

      this.statusService.getDeEnrollCodes()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            console.log('De-enrollment codes loaded:', response);
            this.isLoadingDeEnrollCodes = false;
          },
          error: (error) => {
            console.error('Error loading de-enrollment codes:', error);
            this.isLoadingDeEnrollCodes = false;
          }
        });
    } else if (this.statusService.getCurrentDeEnrollCodes().length > 0) {
      console.log('De-enrollment codes already loaded, using cache');
    } else {
      console.log('De-enrollment codes already loading, skipping duplicate call');
    }
  }

  private loadTransExceptions(): void {
    // Check if already loaded or currently loading
    if (this.statusService.getCurrentTransExceptions().length === 0 && !this.isLoadingTransExceptions) {
      this.isLoadingTransExceptions = true;
      console.log('Loading transaction exceptions...');

      this.statusService.getTransExceptions()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            console.log('Transaction exceptions loaded:', response);
            this.isLoadingTransExceptions = false;
          },
          error: (error) => {
            console.error('Error loading transaction exceptions:', error);
            this.isLoadingTransExceptions = false;
          }
        });
    } else if (this.statusService.getCurrentTransExceptions().length > 0) {
      console.log('Transaction exceptions already loaded, using cache');
    } else {
      console.log('Transaction exceptions already loading, skipping duplicate call');
    }
  }

  private loadTerminationCodes(): void {
    if (this.statusService.getCurrentTerminationCodes().length === 0 && !this.isLoadingTerminationCodes) {
      this.isLoadingTerminationCodes = true;
      console.log('Loading termination codes...');

      this.statusService.getTerminationCodes()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            console.log('Termination codes loaded:', response);
            this.isLoadingTerminationCodes = false;
          },
          error: (error) => {
            console.error('Error loading termination codes:', error);
            this.isLoadingTerminationCodes = false;
          }
        });
    } else if (this.statusService.getCurrentTerminationCodes().length > 0) {
      console.log('Termination codes already loaded, using cache');
    } else {
      console.log('Termination codes already loading, skipping duplicate call');
    }
  }

  setActiveTab(tab: 'electronic' | 'non-electronic'): void {
    this.activeTab.set(tab);
    // Clear form when switching tabs
    this.statusForm.reset();
    this.statusService.clearCache();

    // Reset signals
    this.selectedStatusSignal.set('');
    this.selectedRejectionReasonSignal.set('');
    this.selectedDeEnrollCodeSignal.set('');
    this.selectedTransExceptionSignal.set('');
    this.selectedTerminationCodeSignal.set('');

    // Reset loading flags
    this.isLoadingTransExceptions = false;
    this.isLoadingRejectionReasons = false;
    this.isLoadingDeEnrollCodes = false;
    this.isLoadingTerminationCodes = false;
    this.isLoadingDropdowns.set(false);
  }

  // Dropdown selection handlers
  onStatusSelected(displayName: string): void {
    const option = this.statusOptions().find(opt => opt.DisplayName === displayName);
    if (option) {
      this.statusForm.patchValue({ status: option.Name });
      this.selectedStatusSignal.set(option.Name);
      this.onStatusChange(option.Name);
    }
  }

  onRejectionReasonSelected(name: string): void {
    const option = this.rejectionReasons().find(opt => opt.Name === name);
    if (option) {
      this.statusForm.patchValue({ rejectionReason: option.id });
      this.selectedRejectionReasonSignal.set(name);
    }
  }

  onDeEnrollCodeSelected(name: string): void {
    const option = this.deEnrollCodes().find(opt => opt.Name === name);
    if (option) {
      this.statusForm.patchValue({ deEnrollCode: option.id });
      this.selectedDeEnrollCodeSignal.set(name);
    }
  }

  onTransExceptionSelected(name: string): void {
    const option = this.transExceptions().find(opt => opt.Name === name);
    if (option) {
      this.statusForm.patchValue({ transException: option.id });
      this.selectedTransExceptionSignal.set(name);
    }
  }

  onTerminationCodeSelected(name: string): void {
    const option = this.terminationCodes().find(opt => opt.Name === name);
    if (option) {
      this.statusForm.patchValue({ terminationCode: option.id });
      this.selectedTerminationCodeSignal.set(name);
    }
  }

  onSubmit(): void {
    if (this.statusForm.valid && !this.isLoading()) {
      this.processStatusUpdate();
    }
  }


    getCustomerId(): string | null {
    return sessionStorage.getItem('CustomerId') ?? localStorage.getItem('CustomerId');
  }
  // Direct submit logic matching the old codebase
  private async processStatusUpdate(): Promise<void> {
    this.isLoading.set(true);
    this.updateFormState();

    try {
      // Get form data and clean it based on selected status (exact logic from old codebase)
      let data = { ...this.statusForm.value, "status": await this.selectedStatus() };
      console.log(data, "data")
      // Clean up data based on status selection (matching old codebase lines 527-537)
      if (data.status === "Suspend") {
        delete data.rejectionReason;
        delete data.transException;
        delete data.terminationCode;
      } else if (data.status === "Deactivated") {
        delete data.transException;
      } else if (data.status === "Active") {
        delete data.rejectionReason;
        delete data.deEnrollCode;
        delete data.terminationCode;
      }

      console.log('Cleaned form data for submission:', data);

      // Add CustomerId to the data
      const updateRequest: CustomerStatusUpdateRequest = {
        ...data,
        CustomerId: this.getCustomerId()
      };

      // Call service with cleaned data and tab type (matching old codebase line 538-540)
      const response = await this.statusService.updateCustomerStatus(updateRequest, this.activeTab()).toPromise();

      if (response.Status) {
        // Success handling (matching old codebase lines 543-548)
        this.toastService.success('Success', 'Status updated successfully!');

        // Trigger customer data refresh to update side panel
        this.customerDataRefreshService.triggerRefresh('status-change', this.customerData?.customerId);

        // Emit status updated event (equivalent to refreshing customer data)
        this.statusUpdated.emit();
      } else {
        // Error handling (exact logic from old codebase lines 550-560)
        if (typeof response?.message === "string") {
          this.toastService.error('Status Update Failed', response.message);
        } else if (typeof response?.message === "object") {
          this.toastService.error('Status Update Failed', response.message || 'An error occurred');
        } else {
          this.toastService.error('Status Update Failed', 'An unknown error occurred');
        }
      }
    } catch (error) {
      console.error('Status update error:', error);
      // Matching old codebase error handling (line 563)
      this.toastService.error('Error', error?.message || 'Failed to update status');
    } finally {
      // Matching old codebase loader handling (line 565)
      this.isLoading.set(false);
      this.updateFormState();
    }
  }

  // Removed confirmStatusUpdate - now using direct submit logic matching old codebase

  // Removed updateCustomerStatus - now using processStatusUpdate with old codebase logic

  // Status checking helper methods
  isStatusDeactivated(status: string): boolean {
    if (!status) return false;
    const lowerStatus = status.toLowerCase();
    return lowerStatus === 'deactivated' ||
      lowerStatus === 'deactivate' ||
      lowerStatus === 'inactive' ||
      lowerStatus.includes('deactiv');
  }

  isStatusSuspended(status: string): boolean {
    if (!status) return false;
    const lowerStatus = status.toLowerCase();
    return lowerStatus === 'suspended' ||
      lowerStatus === 'suspend' ||
      lowerStatus.includes('suspend');
  }

  isStatusActive(status: string): boolean {
    if (!status) return false;
    const lowerStatus = status.toLowerCase();
    // Exclude 'deactiv*' variants so only genuine Active statuses match
    if (lowerStatus.includes('deactiv')) return false;
    return lowerStatus === 'active' ||
      lowerStatus === 'activate' ||
      lowerStatus.includes('activ');
  }

  // Check permissions for customer status changes.
  // Status permissions live under the "Customer-Line-Status" resource category →
  // its first resource_type ("Dropdown Permission") → resources
  // (ACTIVE / NEW / PENDING / SUSPENDED / DEACTIVATED). This mirrors how
  // report-details.component.ts reads the same node.
  async checkCustomerStatusPermissions(): Promise<CustomerStatusPermissions> {
    try {
      let category: any = await this.permissionService
        .getResourceCategoryById('Customer-Line-Status')
        .catch(() => null);

      if (!category?.resource_types) {
        const all = await this.permissionService.getAllResourceCategories().catch(() => []);
        category = (all || []).find((c: any) => c?.Name === 'Customer-Line-Status');
      }

      const statusResources: any[] = category?.resource_types?.[0]?.resources || [];

      if (statusResources.length === 0) {
        console.warn('No resources found under Customer-Line-Status for status permissions');
        return this.getDefaultStatusPermissions();
      }

      console.log('Status Resources from Customer-Line-Status:', statusResources);

      const permissions: CustomerStatusPermissions = {
        new: false,
        active: false,
        suspended: false,
        deactivated: false,
        pendingActivation: false
      };

      // Match by resource Name (ORDERTYPE / ALL are not status entries → ignored)
      statusResources.forEach((resource: any) => {
        const name = (resource?.Name || resource?.DisplayName || '').toUpperCase();
        const canView =
          resource?.resource_permission?.[0]?.CanView === 1 ||
          resource?.operations?.can_view === true;

        switch (name) {
          case 'NEW':
            permissions.new = canView;
            break;
          case 'ACTIVE':
            permissions.active = canView;
            break;
          case 'SUSPENDED':
            permissions.suspended = canView;
            break;
          case 'DEACTIVATED':
            permissions.deactivated = canView;
            break;
          case 'PENDING':
            permissions.pendingActivation = canView;
            break;
        }
      });

      console.log('Customer Status Permissions (Customer-Line-Status):', permissions);
      return permissions;

    } catch (error) {
      console.error('Error checking customer status permissions:', error);
      return this.getDefaultStatusPermissions();
    }
  }

  private getDefaultStatusPermissions(): CustomerStatusPermissions {
    return {
      new: false,
      active: false,
      suspended: false,
      deactivated: false,
      pendingActivation: false
    };
  }

  // Filter status options based on permissions (V1 Pattern)
  private filterStatusOptionsByPermissions(statusOptions: StatusOption[]): StatusOption[] {
    // Safety check: if no status options provided, return empty array
    if (!statusOptions || statusOptions.length === 0) {
      console.log('No status options provided to filter');
      return [];
    }

    // Log current permissions for debugging
    console.log('Status Permissions Check:', {
      new: this.statusPermissions.new,
      active: this.statusPermissions.active,
      suspended: this.statusPermissions.suspended,
      deactivated: this.statusPermissions.deactivated,
      pendingActivation: this.statusPermissions.pendingActivation
    });

    // Filter statuses based on permissions
    const filteredStatuses = statusOptions.filter(status => {
      const displayName = (status.DisplayName || status.Name || '').toLowerCase();

      // Match by DisplayName (V1 pattern)
      if (displayName.includes('new')) {
        return this.statusPermissions.new;
      } else if (displayName.includes('active') && !displayName.includes('deactiv') && !displayName.includes('pending')) {
        return this.statusPermissions.active;
      } else if (displayName.includes('suspend')) {
        return this.statusPermissions.suspended;
      } else if (displayName.includes('deactiv') || displayName.includes('inactive')) {
        return this.statusPermissions.deactivated;
      } else if (displayName.includes('pending')) {
        return this.statusPermissions.pendingActivation;
      }

      // For unknown statuses, don't show them
      console.warn(`Unknown status "${status.Name}" - not showing`);
      return false;
    });

    console.log('Filtered statuses:', filteredStatuses.map(s => s.DisplayName || s.Name));
    return filteredStatuses;
  }

  onCancel(): void {
    this.statusService.clearCache();
    this.cancelled.emit();
  }
}
